/**
 * `prices` — the tariff rows.
 *
 * Numbers are printed EXACTLY as they arrive. The date, the weekday, the day
 * type and the tier order are decided in the graph's code and pasted; three
 * live bugs (a wrong weekend tariff, a relabelled tier, a dropped row) were all
 * a paraphrase rather than a wrong lookup, and a frontend that reformats is one
 * more place to paraphrase from.
 *
 * `highlight` (design doc §11.6) picks rows out of the FULL table by their own
 * `key` — never a filter, `CollapsibleRows` only ever decides which of the
 * already-complete `rows` are visible before a tap. `actions` (§11.4/§11.5)
 * ride through the shared `CardActions`, next to `buy_url`'s own link.
 */

import type { PriceTableProps } from '../generated/schemas';
import { cn } from '../../lib/cn';
import { stripEmoji as plain } from '../../lib/emoji';
import CardActions from './CardActions';
import CollapsibleRows from './CollapsibleRows';

type PriceRow = PriceTableProps['rows'][number];

export default function PriceTable({ props }: { props: PriceTableProps }) {
  const links = props.buy_url ? [{ label: 'Купить билет', url: props.buy_url }] : [];

  return (
    <article className="card card--prices">
      <header className="card__head">
        <h3 className="card__title">{plain(props.title ?? 'Билеты')}</h3>
        {props.date_label ? (
          <span className="card__meta">
            {plain(props.date_label)}
            {props.weekday_label ? `, ${plain(props.weekday_label)}` : ''}
          </span>
        ) : null}
      </header>
      {props.is_weekend ? <p className="notice notice--info">Действует тариф выходного дня</p> : null}
      <div className="table-wrap">
        <table className="table">
          <tbody>
            <CollapsibleRows<PriceRow>
              rows={props.rows}
              highlight={props.highlight}
              keyOf={(row) => row.key}
              renderRow={(row, { highlighted, revealed }) => (
                <tr
                  key={`${row.title}:${row.price}`}
                  className={cn(highlighted && 'table__row--highlight', revealed && 'row-reveal')}
                >
                  <th scope="row">
                    {plain(row.title)}
                    {row.note ? <span className="table__note">{plain(row.note)}</span> : null}
                  </th>
                  <td>{plain(row.price)}</td>
                </tr>
              )}
              renderToggle={(button) => (
                <tr className="table__disclosure-row">
                  <td colSpan={2}>{button}</td>
                </tr>
              )}
            />
          </tbody>
        </table>
      </div>
      {(props.notes ?? []).length > 0 ? (
        <ul className="card__notes">
          {(props.notes ?? []).map((note) => (
            <li key={note}>{plain(note)}</li>
          ))}
        </ul>
      ) : null}
      <CardActions sourceId={props.id} actions={props.actions} links={links} />
    </article>
  );
}
